import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { Pagination } from "@/components/ui/pagination";
import DashboardWrapper from "../../components/Layout/DashboardWrapper";
import ModalUI from "../../components/legacy/Modal";
import VisitReasonBadge from "../../components/VisitReason/VisitReasonBadge";
import VisitReasonFormModal from "../../components/VisitReason/VisitReasonFormModal";
import { VISIT_REASONS_LOOKUP_KEY } from "../../hooks/queries/useVisitReasonsLookup";
import {
  createVisitReason,
  deleteVisitReason,
  getVisitReasons,
  updateVisitReason,
} from "../../services/visitReasons";
import { VisitReasonApi, VisitReasonPayload } from "../../services/visitReasons/types";

const PAGE_SIZE = 20;
const LIST_QUERY_KEY = "visit-reasons";

/** Fenêtre de saisie : fermée, création, ou modification d'un motif. */
type FormState = { mode: "closed" } | { mode: "create" } | { mode: "edit"; visitReason: VisitReasonApi };

/**
 * Gestion du catalogue des motifs de visite (médecin, administrateur) : ajout,
 * modification, désactivation, suppression. Les motifs actifs sont ceux que la
 * secrétaire peut choisir à l'ajout d'un patient dans la file d'attente.
 */
const VisitReasons: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [form, setForm] = useState<FormState>({ mode: "closed" });
  const [deleteCandidate, setDeleteCandidate] = useState<VisitReasonApi | null>(null);

  const { data: result, isLoading } = useQuery([LIST_QUERY_KEY, page], () =>
    getVisitReasons({ page, size: PAGE_SIZE })
  );
  const paginatedData = (result as any)?.data || {};
  const visitReasons: VisitReasonApi[] = paginatedData.content || [];

  const refresh = () => {
    queryClient.invalidateQueries([LIST_QUERY_KEY]);
    queryClient.invalidateQueries(VISIT_REASONS_LOOKUP_KEY);
  };

  const saveMut = useMutation(
    (payload: VisitReasonPayload) =>
      form.mode === "edit"
        ? updateVisitReason(form.visitReason.id, payload)
        : createVisitReason(payload),
    {
      onSuccess: (res: any) => {
        if (!res?.ok) return;
        toast.success(form.mode === "edit" ? "Motif modifié" : "Motif ajouté");
        setForm({ mode: "closed" });
        refresh();
      },
    }
  );

  const deleteMut = useMutation((id: number) => deleteVisitReason(id), {
    onSuccess: (res: any) => {
      if (!res?.ok) return;
      toast.success("Motif supprimé");
      setDeleteCandidate(null);
      refresh();
    },
  });

  return (
    <DashboardWrapper showSpinner={isLoading}>
      <>
        <div className="space-y-6">
          <div className="flex flex-wrap items-end justify-between gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
            <div>
              <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">
                Liste d'attente
              </p>
              <h1 className="text-2xl font-semibold text-slate-900">Motifs de visite</h1>
              <p className="text-sm text-slate-500 mt-1">
                Motifs proposés à l'accueil quand un patient est ajouté à la liste d'attente.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ mode: "create" })}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white px-5 py-3 rounded-xl font-medium shadow-lg shadow-green-600/25 transition-all duration-300 focus:ring-4 focus:ring-emerald-500/30"
            >
              <i className="bx bx-plus text-lg" /> Nouveau motif
            </button>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-500 delay-100 fill-mode-both">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="px-5 py-3">Ordre</th>
                  <th className="px-5 py-3">Motif</th>
                  <th className="px-5 py-3">À l'accueil</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visitReasons.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={4} className="px-5 py-10 text-center text-slate-400">
                      Aucun motif. Ajoutez-en un pour la liste d'attente.
                    </td>
                  </tr>
                )}
                {visitReasons.map((visitReason) => (
                  <tr
                    key={visitReason.id}
                    className="group/row border-t border-slate-100 hover:bg-emerald-50/40 transition-colors"
                  >
                    <td className="px-5 py-3 text-slate-500 tabular-nums">{visitReason.displayOrder}</td>
                    <td className="px-5 py-3">
                      <VisitReasonBadge label={visitReason.label} color={visitReason.color} />
                    </td>
                    <td className="px-5 py-3">
                      {visitReason.activated ? (
                        <span className="text-emerald-700 font-medium">Proposé</span>
                      ) : (
                        <span className="text-slate-400">Désactivé</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1 opacity-70 group-hover/row:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={() => setForm({ mode: "edit", visitReason })}
                          title="Modifier"
                          aria-label={`Modifier ${visitReason.label}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100"
                        >
                          <i className="bx bx-edit-alt text-lg" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteCandidate(visitReason)}
                          title="Supprimer"
                          aria-label={`Supprimer ${visitReason.label}`}
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-rose-600 hover:bg-rose-50"
                        >
                          <i className="bx bx-trash text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={paginatedData.currentPage || 0}
            totalPages={paginatedData.totalPages || 0}
            totalElements={paginatedData.totalElements || 0}
            currentCount={visitReasons.length}
            hasNext={paginatedData.hasNext || false}
            hasPrevious={paginatedData.hasPrevious || false}
            onPageChange={setPage}
            itemLabel="motif"
            isLoading={isLoading}
          />
        </div>

        <VisitReasonFormModal
          key={form.mode === "edit" ? form.visitReason.id : form.mode}
          isOpen={form.mode !== "closed"}
          visitReason={form.mode === "edit" ? form.visitReason : null}
          isSaving={saveMut.isLoading}
          onSubmit={(payload) => saveMut.mutate(payload)}
          onClose={() => setForm({ mode: "closed" })}
        />

        <ModalUI
          isOpen={deleteCandidate != null}
          onClose={() => {
            if (!deleteMut.isLoading) setDeleteCandidate(null);
          }}
          styles={{ width: "min(440px, 92vw)", padding: 0 }}
        >
          <div className="flex flex-col">
            <div className="px-6 pt-6 space-y-2">
              <h2 className="text-lg font-semibold text-slate-900">Supprimer ce motif ?</h2>
              <p className="text-sm text-slate-500">
                <span className="font-medium text-slate-800">{deleteCandidate?.label}</span> ne sera plus
                proposé à l'accueil. Les patients déjà enregistrés avec ce motif le gardent. Pour le
                masquer temporairement, préférez le désactiver.
              </p>
            </div>
            <div className="mt-6 flex items-center gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
              <button
                type="button"
                onClick={() => setDeleteCandidate(null)}
                disabled={deleteMut.isLoading}
                className="flex-1 h-11 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => deleteCandidate && deleteMut.mutate(deleteCandidate.id)}
                disabled={deleteMut.isLoading}
                className="flex-[1.4] inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium shadow-lg shadow-rose-600/25 disabled:opacity-70"
              >
                <i className="bx bx-trash text-base" /> Supprimer
              </button>
            </div>
          </div>
        </ModalUI>
      </>
    </DashboardWrapper>
  );
};

export default VisitReasons;
